import { useCallback } from 'react'
import { Loader2, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  useUnlinkProductComponent,
  type ProductComponentLink,
} from '@/shared/hooks/api/catalog'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'

type V2RemoveProductPartDialogProps = {
  productId: string
  part: ProductComponentLink | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onRemoved?: () => void
}

export function V2RemoveProductPartDialog({
  productId,
  part,
  open,
  onOpenChange,
  onRemoved,
}: V2RemoveProductPartDialogProps) {
  const { t } = useTranslation('common')
  const { mutateAsync: unlinkComponent, isPending } =
    useUnlinkProductComponent(productId)

  const sku = part?.component.sku ?? ''
  const name =
    part?.component.name != null && part.component.name.trim() !== ''
      ? part.component.name.trim()
      : t('v2.pages.components.fallbackName')
  const supplier = part?.component.supplier.name ?? ''

  const handleRemove = useCallback(async () => {
    if (part == null) {
      return
    }

    try {
      await unlinkComponent({ componentId: part.componentId })
      toast.success(t('v2.pages.products.detail.parts.remove.success'))
      onOpenChange(false)
      onRemoved?.()
    } catch {
      toast.error(t('v2.pages.products.detail.parts.unlink.errors.failed'))
    }
  }, [onOpenChange, onRemoved, part, t, unlinkComponent])

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('v2.pages.products.detail.parts.remove.confirmTitle')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('v2.pages.products.detail.parts.remove.confirmDescription', {
              sku,
              name,
              supplier,
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {t('actions.cancel')}
          </AlertDialogCancel>
          <Button
            type="button"
            variant="destructive"
            disabled={isPending || part == null}
            className="gap-2"
            onClick={() => {
              void handleRemove()
            }}
          >
            {isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                {t('v2.pages.products.detail.parts.remove.loading')}
              </>
            ) : (
              <>
                <Trash2 className="size-4" aria-hidden />
                {t('v2.pages.products.detail.parts.remove.confirmButton')}
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
